import { useEffect, useState } from "react";
import { client } from "../net/client";
import { useSignal } from "../net/signal";
import { fa, faNum } from "../game/cards";
import { games } from "./games";
import { hasMultiplayer, liveGames, loadAvailability, offOnline, offSite } from "./availability";
import { avatar, avatarSrc } from "../game/avatar";
import { WalletBar } from "./Wallet";
import { Today } from "./Today";
import { AuthGate, useRegistered } from "./Account";
import { STAKES, loadPrefs, savePrefs } from "./prefs";

/**
 * لابی — چیدنِ میز و بازی، و هیچ چیزِ دیگر.
 *
 * هرچه «درباره‌ی من» بود به لندینگ رفت: پشتیبانی، صندوق، اشتراک، خروج از
 * حساب. این‌جا فقط سه تصمیم مانده — کدام بازی، چقدر ورودی، با چه کسی — و
 * همان سه تا **یادشان می‌مانَد**: بازیکنی که هر شب یک جور بازی می‌کند نباید
 * هر شب همان سه انتخاب را از نو بکند.
 */
export function Hub({ onProfile, onShop }: { onProfile: () => void; onShop: () => void }) {
  const saved = useState(() => loadPrefs(gameFromUrl()))[0];
  // بازی‌هایی که ادمین خاموش کرده از قفسه می‌روند — [[loadAvailability]].
  const offS = useSignal(offSite);
  const offO = useSignal(offOnline);
  const shelf = liveGames(offS, offO);
  useEffect(() => { void loadAvailability(); }, []);
  const [game, setGame] = useState(saved.game);
  const [stake, setStake] = useState(saved.stake);
  const [open, setOpen] = useState(saved.open);
  const [locked, setLocked] = useState(saved.locked);

  // بازیِ انتخاب‌شده خاموش شد؟ به اولین بازیِ قفسه برگرد.
  //
  // بی این، انتخابِ ذخیره‌شده‌ی دیشب روی بازی‌ای می‌مانْد که دیگر روی قفسه
  // نیست: هیچ دکمه‌ای روشن نبود و «بازیِ سریع» میزی می‌خواست که سرور ردش
  // می‌کرد — بی آن‌که بازیکن بفهمد چرا.
  useEffect(() => {
    if (shelf.length === 0) return;
    if (!shelf.some((g) => g.id === game)) setGame(shelf[0]!.id);
  }, [shelf, game]);
  // رمزِ اتاق ذخیره **نمی‌شود**: چیزی که بین دو نفر رد و بدل می‌شود نباید در
  // مرورگر بماند، و اتاقِ بعدی هم رمزِ خودش را می‌خواهد.
  const [pass, setPass] = useState("");
  const [code, setCode] = useState("");
  const name = useSignal(client.playerName);
  const match = useSignal(client.match);
  const myAvatar = useSignal(avatar);
  const wallet = useSignal(client.wallet);
  const searching = match?.state === "searching";
  // مهمان تماشا می‌کند، سرِ میز نمی‌نشیند: دکمه‌های بازی جایشان را به دروازه می‌دهند.
  const registered = useRegistered();

  useEffect(() => { savePrefs({ game, stake, open, locked }); }, [game, stake, open, locked]);

  const coins = wallet?.coins ?? 0n;
  const stakeNote = stake > 0n ? ` — ورودی ${faNum(stake)}` : "";

  return (
    <div className="page stack">
      <WalletBar />

      {/* کی هستم: چهره و نام — هر دو در را به پروفایل باز می‌کنند.
          پیش‌تر نام همین‌جا جعبه‌ی تایپ بود و هر حرف ذخیره می‌شد. حالا سقفِ
          هفت‌روزه‌ی سرور دارد، و کاری که در هفته یک بار می‌شود نباید با
          یک کلیکِ تصادفی انجام شود. */}
      <div className="plate row" style={{ gap: 10 }}>
        <button className="facebtn" onClick={onProfile} aria-label="پروفایلِ من" title="پروفایلِ من">
          <img src={avatarSrc(myAvatar)} alt="" width={44} height={44} />
        </button>
        <button className="namebtn" onClick={onProfile} aria-label="نام و پروفایلِ من">
          {name || "مهمان"}
        </button>
        {/* فروشگاه کنارِ نام و نه ته صفحه: سکه در همین نوار دیده می‌شود و
            جایی که سکه را می‌بینی همان‌جاست که خرج‌کردنش به فکرت می‌رسد. */}
        <button className="hb" onClick={onShop} aria-label="فروشگاه" title="فروشگاه">فروشگاه</button>
      </div>

      {/* «امروز» بالای قفسه و نه زیرش: چیزی که فقط تا نیمه‌شب هست باید پیش
          از چیزی دیده شود که همیشه هست. */}
      <Today onPlay={(g) => { setGame(g); client.quickMatch(g, 0n); }} />

      {/* انتخابِ بازی. حالتِ انتخاب با `aria-pressed` هم گفته می‌شود، نه فقط با
          رنگ: با صفحه‌خوان وگرنه هر سه یک‌جور خوانده می‌شدند. */}
      <div className="row" style={{ gap: 10 }} role="group" aria-label="بازی">
        {shelf.map((g) => (
          <button
            key={g.id}
            className={"plate gamebtn" + (game === g.id ? " on" : "")}
            // نامِ دکمه صریح: محتوایش دو تکه است (نام و زیرنویس) و
            // صفحه‌خوان هر سه را یک‌جور می‌خواند.
            aria-label={g.fa}
            aria-pressed={game === g.id}
            style={{ background: g.color }}
            onClick={() => setGame(g.id)}
          >
            <div style={{ fontSize: 19, fontWeight: 700 }}>{g.fa}</div>
            <div className="muted" style={{ color: "#ffffffb0" }}>{g.note}</div>
          </button>
        ))}
      </div>

      {!registered ? (
        <AuthGate />
      ) : searching ? (
        <div className="plate stack">
          <div>در جست‌وجوی هم‌بازی… {fa(match.seated)} از {fa(match.seats)}</div>
          {match.botFillInSec > 0 && (
            <div className="muted">تا {fa(match.botFillInSec)} ثانیه‌ی دیگر جای خالی با ربات پر می‌شود.</div>
          )}
          <button className="btn ghost" onClick={() => client.cancelMatch()}>بی‌خیال</button>
        </div>
      ) : !hasMultiplayer(offO, game) ? (
        /* بازی‌ای که ادمین از آنلاین درآورده: نه ورودی، نه صف، نه اتاق —
           حریفی جز ربات نیست و نشان‌دادنِ آن سه تا فقط وعده‌ی دروغ است. */
        <div className="stack" style={{ gap: 10 }}>
          <button className="btn" onClick={() => client.soloMatch(game)}>
            شروع با ربات‌ها
          </button>
          <div className="muted">
            این بازی فعلاً چندنفره ندارد؛ میز با ربات‌ها پر می‌شود و ورودیِ سکه‌ای هم نمی‌خواهد.
          </div>
        </div>
      ) : (<>
        {/* ورودیِ میز بالای هر دو راهِ نشستن است، چون تصمیمی است که پیش از
            فشردنِ دکمه گرفته می‌شود. دو پیکرِ جدا یعنی بازیکن دو بار همان
            تصمیم را می‌گیرد و یک بار هم یادش می‌رود کدامش را عوض کرده. */}
        <div className="stack" style={{ gap: 6 }}>
          <div className="row" style={{ gap: 8, alignItems: "center" }}>
            <span className="muted">ورودیِ میز</span>
            {STAKES.map((s) => (
              <button
                key={String(s)}
                className={"chip" + (s === stake ? " on" : "")}
                aria-pressed={s === stake}
                disabled={s > coins}
                title={s > coins ? "سکه کافی نیست" : undefined}
                onClick={() => setStake(s)}
              >
                {s === 0n ? "رایگان" : faNum(s)}
              </button>
            ))}
          </div>
          {stake > 0n && (
            <div className="muted">
              هر آدمِ سرِ میز {faNum(stake)} سکه می‌گذارد و برنده‌ها تقسیم می‌کنند (۱۰٪ برشِ خانه).
              ربات نه پول می‌گذارد نه می‌برد. میز دستِ‌کم دو آدم می‌خواهد، وگرنه رایگان است.
            </div>
          )}
        </div>

        <button className="btn" onClick={() => client.quickMatch(game, stake)}>
          بازیِ سریع{stakeNote}
        </button>

        {/* اتاقِ خصوصی — راهِ دوم، و عمداً جمع‌وجورتر: بیشترِ دست‌ها با
            «بازیِ سریع» شروع می‌شوند و این نباید هم‌وزنِ آن دیده شود.
            دو انتخابش چیپ‌اند و نه چک‌باکسِ یک‌خط‌درمیان — هم کم‌جاترند، هم
            حالتشان با یک نگاه خوانده می‌شود. */}
        <div className="plate stack" style={{ gap: 10 }}>
          <div className="muted">اتاقِ خصوصی با کد</div>
          <div className="row" style={{ gap: 8 }} role="group" aria-label="گزینه‌های اتاق">
            {/* توضیح زیرِ چیپ‌ها می‌آید و نه در `title`: روی گوشی تولتیپ
                هیچ‌وقت دیده نمی‌شود، و صفحه‌خوان هم به‌جای نامِ دکمه همان
                متنِ بلند را می‌خواند. */}
            <button
              className={"chip" + (open ? " on" : "")}
              aria-pressed={open}
              onClick={() => setOpen((v) => !v)}
            >
              تماشایی
            </button>
            <button
              className={"chip" + (locked ? " on" : "")}
              aria-pressed={locked}
              onClick={() => setLocked((v) => { if (v) setPass(""); return !v; })}
            >
              رمزدار
            </button>
          </div>
          <div className="muted">
            {open ? "بقیه می‌توانند تماشا کنند؛ دستِ کسی دیده نمی‌شود." : "فقط خودتان؛ در فهرستِ تماشا نمی‌آید."}
            {locked ? " هر کس کد را دارد هم بی رمز نمی‌نشیند." : ""}
          </div>
          {locked && (
            <input
              type="password"
              value={pass}
              placeholder="رمزِ اتاق"
              aria-label="رمزِ اتاق"
              maxLength={64}
              autoComplete="off"
              onChange={(e) => setPass(e.target.value)}
            />
          )}
          <button
            className="btn teal"
            disabled={locked && pass.trim().length === 0}
            onClick={() => client.createRoom(game, 4, "eyvan", stake, open, locked ? pass : "")}
          >
            ساختنِ اتاق{stakeNote}
          </button>
        </div>

        <div className="plate stack">
          <div className="muted">کدِ اتاقی داری؟</div>
          <div className="row">
            <input
              className="code"
              inputMode="numeric"
              maxLength={6}
              placeholder="۶ رقم"
              aria-label="کدِ اتاق"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            />
            <button
              className="btn"
              style={{ width: "auto", paddingInline: 20 }}
              disabled={code.length !== 6}
              onClick={() => { client.joinRoom(code); setCode(""); }}
            >
              پیوستن
            </button>
          </div>
        </div>
      </>)}
    </div>
  );
}

/**
 * بازیِ خواسته‌شده در نشانی.
 *
 * صفحه‌ی هر بازی در سایت به `/play?game=<id>` می‌رود؛ بی این، بازیکن روی
 * بازیِ دفعه‌ی پیش می‌نشست هرچقدر هم که از صفحه‌ی شلم آمده باشد.
 */
function gameFromUrl(): string | null {
  try {
    const q = new URLSearchParams(location.search).get("game");
    if (q && games.some((g) => g.id === q)) return q;
  } catch { /* بی‌نشانی */ }
  return null;
}
